/**
 * @module features/display-module/pages/DisplayModulePage
 * @description Primary Client & Super Admin workspace for managing Digital Display Module (DDM)
 * artifacts across all 7 types (`notice`, `birthday`, `emergency_alert`, `quote`, `gallery`,
 * `custom_banner`, `event`). Enforces the 4-State UI Contract (`Loading`, `Error`, `Empty`,
 * `Populated`), adaptive Mobile/Tablet/Desktop layout, and white-label `theme_color` (`T017`).
 */

import * as React from "react";
import {
  Plus,
  Search,
  RefreshCw,
  MonitorSmartphone,
  Radio,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/features/auth";
import { applyClientThemeColor } from "@/features/clients";
import {
  useAssignedDdmDevices,
  useCreateDdmNotification,
  useDdmNotifications,
  useDeleteDdmNotification,
  useUpdateDdmNotification,
  type CreateOrUpdateDdmArtifactInput,
} from "../api/ddmApi";
import {
  DDM_ARTIFACT_TYPES,
  DDM_ARTIFACT_TYPE_META,
  type DdmArtifactType,
  type DdmDeliveryStatus,
  type DdmNotificationRecord,
} from "../types/ddm";
import { DdmSkeleton } from "../components/DdmSkeleton";
import { DdmNotificationTable } from "../components/DdmNotificationTable";
import { DdmArtifactFormModal } from "../components/DdmArtifactFormModal";

export function DisplayModulePage() {
  const { user } = useAuth();

  // Apply Client Organization white-label theme_color when signed into Client Portal
  React.useEffect(() => {
    const clientTheme = user?.client?.theme_color;
    if (clientTheme && /^#[0-9a-fA-F]{6}$/.test(clientTheme)) {
      applyClientThemeColor(clientTheme);
    }
  }, [user?.client?.theme_color]);

  const [selectedDeviceId, setSelectedDeviceId] = React.useState<string>("all");
  const [selectedType, setSelectedType] = React.useState<
    DdmArtifactType | "all"
  >("all");
  const [selectedStatus, setSelectedStatus] = React.useState<
    DdmDeliveryStatus | "all"
  >("all");
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [page, setPage] = React.useState<number>(1);

  const [isFormOpen, setIsFormOpen] = React.useState<boolean>(false);
  const [editingRecord, setEditingRecord] =
    React.useState<DdmNotificationRecord | null>(null);
  const [deletingRecord, setDeletingRecord] =
    React.useState<DdmNotificationRecord | null>(null);
  const [updatingId, setUpdatingId] = React.useState<string | null>(null);

  const devicesQuery = useAssignedDdmDevices();
  const notificationsQuery = useDdmNotifications({
    page,
    page_size: 20,
    device_id: selectedDeviceId,
    artifact_type: selectedType,
    delivery_status: selectedStatus,
    search: searchTerm,
  });

  const createMutation = useCreateDdmNotification();
  const updateMutation = useUpdateDdmNotification();
  const deleteMutation = useDeleteDdmNotification();

  const devices = React.useMemo(
    () => devicesQuery.data ?? [],
    [devicesQuery.data]
  );
  const items = React.useMemo(
    () => notificationsQuery.data?.items ?? [],
    [notificationsQuery.data?.items]
  );
  const pagination = notificationsQuery.data?.pagination ?? {
    page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  };

  // Summary metrics derived from current dataset
  const stats = React.useMemo(() => {
    let liveCount = 0;
    let ackedCount = 0;
    let syncingCount = 0;
    for (const item of items) {
      if (item.delivery_status === "LIVE") liveCount += 1;
      if (item.device_received) ackedCount += 1;
      if (
        item.delivery_status === "PENDING_DEVICE_ACK" ||
        item.delivery_status === "UPDATE_PENDING_ACK"
      ) {
        syncingCount += 1;
      }
    }
    return {
      assignedDevices: devices.length,
      totalArtifacts: pagination.total_items,
      liveCount,
      ackedCount,
      syncingCount,
    };
  }, [devices.length, items, pagination.total_items]);

  const handleOpenCreate = React.useCallback(() => {
    setEditingRecord(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = React.useCallback((record: DdmNotificationRecord) => {
    setEditingRecord(record);
    setIsFormOpen(true);
  }, []);

  const handleCreateOrUpdate = React.useCallback(
    async (input: CreateOrUpdateDdmArtifactInput, editId?: string) => {
      if (editId) {
        await updateMutation.mutateAsync({ id: editId, input });
      } else {
        await createMutation.mutateAsync(input);
      }
    },
    [createMutation, updateMutation]
  );

  const handleToggleEmergencyPause = React.useCallback(
    async (record: DdmNotificationRecord) => {
      try {
        setUpdatingId(record.id);
        await updateMutation.mutateAsync({
          id: record.id,
          input: {
            payload: {
              pause_emergency_alert: !record.pause_emergency_alert,
            },
          },
        });
      } finally {
        setUpdatingId(null);
      }
    },
    [updateMutation]
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deletingRecord) return;
    try {
      await deleteMutation.mutateAsync(deletingRecord.id);
      setDeletingRecord(null);
    } catch {
      // Error surfaced via mutation state
    }
  }, [deleteMutation, deletingRecord]);

  const hasActiveFilters =
    selectedDeviceId !== "all" ||
    selectedType !== "all" ||
    selectedStatus !== "all" ||
    Boolean(searchTerm.trim());

  const clearFilters = () => {
    setSelectedDeviceId("all");
    setSelectedType("all");
    setSelectedStatus("all");
    setSearchTerm("");
    setPage(1);
  };

  const isLoading = devicesQuery.isLoading || notificationsQuery.isLoading;
  const isError = devicesQuery.isError || notificationsQuery.isError;
  const errorObj = devicesQuery.error || notificationsQuery.error;

  return (
    <section
      aria-label="Digital Display Module Workspace"
      className="space-y-6"
    >
      {/* Top Workspace Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
              Display Module
            </h1>
            {user?.client?.org_name && (
              <span
                style={{
                  borderColor: "var(--color-brand-primary)",
                }}
                className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-border bg-muted/50 text-foreground"
              >
                {user?.client?.org_name}
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Schedule notices, birthdays, emergency alerts, quotes, galleries,
            banners, and events with mutually signed real-time device sync.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              devicesQuery.refetch();
              notificationsQuery.refetch();
            }}
            className="h-10 px-3 rounded-xl text-xs gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            type="button"
            onClick={handleOpenCreate}
            disabled={devices.length === 0}
            style={{
              backgroundColor: "var(--color-brand-primary)",
              color: "var(--color-brand-foreground)",
            }}
            className="h-10 px-4 rounded-xl text-xs font-semibold bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 gap-1.5 shadow-xs"
          >
            <Plus className="h-4 w-4" />
            <span>New Display Artifact</span>
          </Button>
        </div>
      </div>

      {/* KPI Summary Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 p-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Assigned DDM Units
            </p>
            <p className="text-xl font-bold text-foreground mt-1">
              {stats.assignedDevices}
            </p>
          </div>
          <div className="h-9 w-9 rounded-xl bg-muted/60 dark:bg-zinc-900 flex items-center justify-center text-foreground">
            <MonitorSmartphone className="h-4 w-4" />
          </div>
        </div>

        <div className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 p-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Live on Display
            </p>
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {stats.liveCount}
            </p>
          </div>
          <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
            <Radio className="h-4 w-4" />
          </div>
        </div>

        <div className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 p-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Device Received
            </p>
            <p className="text-xl font-bold text-foreground mt-1">
              {stats.ackedCount}
            </p>
          </div>
          <div className="h-9 w-9 rounded-xl bg-sky-500/10 flex items-center justify-center text-sky-500">
            <CheckCircle2 className="h-4 w-4" />
          </div>
        </div>

        <div className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 p-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Pending Sync Ack
            </p>
            <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {stats.syncingCount}
            </p>
          </div>
          <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
            <RefreshCw className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-2xl border border-border/80 dark:border-zinc-800/80 bg-card dark:bg-zinc-950/80 p-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            placeholder="Search title, remarks, device..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="h-10 w-full rounded-xl border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 pl-9 pr-3 text-xs text-foreground"
          />
        </div>

        {/* Device Filter */}
        <select
          aria-label="Filter by Assigned DDM Device"
          value={selectedDeviceId}
          onChange={(e) => {
            setSelectedDeviceId(e.target.value);
            setPage(1);
          }}
          className="h-10 w-full rounded-xl border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs text-foreground"
        >
          <option value="all">All Assigned DDM Devices</option>
          {devices.map((dev) => (
            <option key={dev.device_id} value={dev.device_id}>
              {dev.device_name} ({dev.registered_device_id})
            </option>
          ))}
        </select>

        {/* Artifact Type Filter */}
        <select
          aria-label="Filter by Artifact Type"
          value={selectedType}
          onChange={(e) => {
            setSelectedType(e.target.value as DdmArtifactType | "all");
            setPage(1);
          }}
          className="h-10 w-full rounded-xl border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs text-foreground"
        >
          <option value="all">All 7 Artifact Types</option>
          {DDM_ARTIFACT_TYPES.map((t) => (
            <option key={t} value={t}>
              {DDM_ARTIFACT_TYPE_META[t].label}
            </option>
          ))}
        </select>

        {/* Delivery Status Filter */}
        <select
          aria-label="Filter by Delivery Status"
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value as DdmDeliveryStatus | "all");
            setPage(1);
          }}
          className="h-10 w-full rounded-xl border border-border/70 dark:border-zinc-800 bg-background dark:bg-zinc-900 px-3 text-xs text-foreground"
        >
          <option value="all">All Sync & Live Statuses</option>
          <option value="LIVE">Live on Display</option>
          <option value="DEVICE_RECEIVED_UPCOMING">
            Device Received · Upcoming
          </option>
          <option value="PENDING_DEVICE_ACK">Pending Initial Device Ack</option>
          <option value="UPDATE_PENDING_ACK">Update Syncing (v2+)</option>
          <option value="PAUSED">Paused (Emergency Alert)</option>
          <option value="EXPIRED">Expired</option>
        </select>
      </div>

      {/* 4-STATE UI CONTRACT */}
      {isLoading ? (
        <DdmSkeleton rows={5} />
      ) : isError ? (
        <div
          role="alert"
          className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Unable to load Display Module artifacts
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {errorObj instanceof Error
                  ? errorObj.message
                  : "A network or server error occurred while fetching display artifacts."}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              devicesQuery.refetch();
              notificationsQuery.refetch();
            }}
            className="h-9 px-4 rounded-xl text-xs font-semibold"
          >
            Retry
          </Button>
        </div>
      ) : devices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border dark:border-zinc-800 bg-card/60 dark:bg-zinc-950/60 p-10 text-center max-w-xl mx-auto my-6 space-y-3">
          <div className="h-12 w-12 rounded-2xl bg-muted dark:bg-zinc-900 flex items-center justify-center mx-auto text-muted-foreground">
            <MonitorSmartphone className="h-6 w-6" />
          </div>
          <h2 className="text-base font-bold text-foreground">
            No Assigned Digital Display Modules Found
          </h2>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your organization does not currently have an actively assigned
            Digital Display Module (`display_module`). Once a DDM hardware unit
            is assigned to your organization, you can schedule notices,
            emergency alerts, galleries, and promotional banners here.
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border dark:border-zinc-800 bg-card/60 dark:bg-zinc-950/60 p-10 text-center max-w-xl mx-auto my-6 space-y-4">
          <div className="h-12 w-12 rounded-2xl bg-muted dark:bg-zinc-900 flex items-center justify-center mx-auto text-muted-foreground">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-foreground">
              {hasActiveFilters
                ? "No Display Artifacts Match Your Filters"
                : "No Display Artifacts Scheduled Yet"}
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {hasActiveFilters
                ? "Try clearing your active filters or search query to view all display artifacts."
                : "Create your first Notice, Birthday, Emergency Alert, Quote, Gallery, Custom Banner, or Event for your assigned display devices."}
            </p>
          </div>
          <div className="flex items-center justify-center gap-2.5">
            {hasActiveFilters ? (
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
                className="h-10 px-4 rounded-xl text-xs font-semibold"
              >
                Clear Filters
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleOpenCreate}
                style={{
                  backgroundColor: "var(--color-brand-primary)",
                  color: "var(--color-brand-foreground)",
                }}
                className="h-10 px-5 rounded-xl text-xs font-semibold bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 gap-1.5"
              >
                <Plus className="h-4 w-4" />
                <span>Create Display Artifact</span>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <DdmNotificationTable
          items={items}
          pagination={pagination}
          onPageChange={setPage}
          onEdit={handleOpenEdit}
          onDelete={setDeletingRecord}
          onToggleEmergencyPause={handleToggleEmergencyPause}
          isUpdatingId={updatingId}
        />
      )}

      {/* Create / Edit Artifact Modal */}
      <DdmArtifactFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        devices={devices}
        editRecord={editingRecord}
        defaultDeviceId={selectedDeviceId}
        onSubmit={handleCreateOrUpdate}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deletingRecord)}
        onOpenChange={(open) => {
          if (!open) setDeletingRecord(null);
        }}
      >
        <DialogContent className="max-w-md p-6 bg-card dark:bg-zinc-950 border-border/80 dark:border-zinc-800 rounded-2xl">
          <DialogHeader className="text-left space-y-2">
            <div className="h-10 w-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center">
              <Trash2 className="h-5 w-5" />
            </div>
            <DialogTitle className="text-base font-bold text-foreground">
              Delete Display Artifact?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
              {deletingRecord?.device_received
                ? `This artifact ("${deletingRecord.search_title}") was already received by ${deletingRecord.device_name}. Deleting it will queue a signed v${deletingRecord.version + 1} removal tombstone so the display removes it on its next sync.`
                : `Are you sure you want to delete "${deletingRecord?.search_title}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row items-center justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletingRecord(null)}
              className="h-9 px-4 rounded-xl text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={handleConfirmDelete}
              className="h-9 px-4 rounded-xl text-xs font-semibold bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Artifact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
